"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  captureFirstTouch,
  clearFirstTouch,
  isAttributionHost,
  rememberAttribution,
  stripStoredClickIds,
} from "@/lib/attribution";
import {
  hasAdvertisingConsent,
  hasAnalyticsConsent,
  onConsentChange,
} from "@/lib/consent";

// Writes the durable `tvt_ft` first-touch cookie on the shared parent domain, so
// the click id, landing URL and external referrer survive the hop to the app. The
// platform reads it server-side in App\Http\Middleware\CaptureAttribution and
// persists it onto user_signup_attributions at signup.
//
// The three steps below are deliberately gated differently:
//
//   - rememberAttribution() runs on every host, before consent, on every route.
//     It only holds the params in memory, so it stores nothing on the device -
//     but without it a visitor who browses to a second page before answering the
//     banner would lose their click id, because by the time consent arrives the
//     params are long gone from the URL.
//   - Withdrawal runs on every host too, in both its forms: the full clear when
//     analytics consent goes, and the click-id strip when only advertising does.
//     The cookie is scoped to the parent domain, so it exists on subdomains that
//     are not allowed to write it, and a withdrawal there must still take effect.
//   - Writing is gated on both consent and an opt-in host allowlist. Preview
//     deploys are subdomains of trein-vertraging.nl as well, and a suffix test
//     would let them seed the production first-touch record in a real visitor's
//     browser - the contamination PostHogProvider's ANALYTICS_HOSTS exists to
//     stop.
//   - The payload is split across TWO OneTrust categories, because it holds two
//     different kinds of thing. The campaign labels (utm_*) ride with analytics
//     consent (C0002). The click ids (gclid / wbraid / gbraid / fbclid / ttclid)
//     are ad-platform identifiers however first-party the cookie itself is, and
//     the container maps advertising onto C0004 - so they need advertising
//     consent, and are stripped again on withdrawal. Both places they can hide
//     are stripped: the dedicated key, and the copy inside the landing URL.
//
// Link stamping is gated more narrowly than this cookie, not left ungated:
// passing on a param the visitor's own current URL already carries stores
// nothing on their device, but recalling one we kept needs advertising consent.
// See attributionParams.
export default function FirstTouchCapture() {
  const pathname = usePathname();

  useEffect(() => {
    rememberAttribution();

    const apply = () => {
      if (!hasAnalyticsConsent()) {
        clearFirstTouch();
        return;
      }
      const advertising = hasAdvertisingConsent();
      // Withdrawing advertising alone runs on EVERY host, for the same reason
      // the full clear above does: the cookie is parent-domain scoped, so it is
      // readable on subdomains that may not create it, and the visitor can open
      // the preference centre on any of them. stripStoredClickIds only ever
      // rewrites a record that already exists, so widening the gate here cannot
      // let a non-allowlisted host seed one.
      if (!advertising) stripStoredClickIds();
      if (isAttributionHost()) captureFirstTouch({ advertising });
    };

    apply();
    return onConsentChange(apply);
  }, [pathname]);

  return null;
}
